import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import Auth from '@aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@material-ui/core';
import { FieldProps, Form, Formik } from 'formik';

import { useNotify, usePublicClient } from 'hooks';
import { Login } from 'Links';
import {
  ActionContainer,
  ClientLogo,
  Layouts,
  Logo,
  SubmitButton,
  ValidatedTextField,
  VisibilityAdornment,
} from 'components';

import { Main } from '../components';
import { ForgotPasswordSubmitSchema, IForgotPasswordSubmitValues } from '../validations';
import { IAuthContainerProps } from '../types';

const ForgotPasswordSubmit: FC<IAuthContainerProps> = ({ authData }) => {
  const notify = useNotify();
  const client = usePublicClient();
  const { t } = useTranslation();

  const [working, setWorking] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const initialValues: IForgotPasswordSubmitValues = {
    code: '',
    password: '',
  };

  async function handleSubmit(values: IForgotPasswordSubmitValues) {
    setWorking(true);

    try {
      await Auth.forgotPasswordSubmit(authData.username, values.code, values.password);
    } catch (error) {
      notify.error(error);
      setWorking(false);
    }
  }

  function renderSidebar() {
    return (
      <Fragment>
        {client ? <ClientLogo client={client} /> : <Logo />}
        <Typography gutterBottom variant="h4" color="textSecondary">
          {t('auth:forgotPasswordSidebarHeader')}
        </Typography>
        <Formik
          key="submit"
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={ForgotPasswordSubmitSchema}
        >
          <Form>
            <ValidatedTextField
              name="code"
              i18nKey="auth:forgotPasswordCode"
              autoComplete="off"
              onChange={(
                event: ChangeEvent<HTMLInputElement>,
                onChange: FieldProps['field']['onChange']
              ) => {
                event.target.value = event.target.value.trim();
                onChange(event);
              }}
            />
            <ValidatedTextField
              name="password"
              type={passwordVisible ? 'text' : 'password'}
              i18nKey="auth:forgotPasswordNewPassword"
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <VisibilityAdornment
                    visible={passwordVisible}
                    onToggle={() => setPasswordVisible(!passwordVisible)}
                  />
                ),
              }}
            />
            <ActionContainer>
              <SubmitButton working={working} text={t('auth:forgotPasswordSubmitButton')} />
              <Button component={Login} fullWidth>
                {t('auth:backToLogin')}
              </Button>
            </ActionContainer>
          </Form>
        </Formik>
      </Fragment>
    );
  }

  return (
    <Layouts.Full2Column
      sidebar={renderSidebar()}
      main={<Main headerI18nKey="auth:bodyHeader1" subHeaderI18nKey="auth:signInSubHeader" />}
    />
  );
};

export default ForgotPasswordSubmit;
